// frontend/src/components/Modals/UserPermissionsModal.jsx

import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
} from 'reactstrap';
import { usersAPI } from '../../services/api';
import { ROLE_NAMES, PERMISSIONS } from '../../utils/permissions';

function UserPermissionsModal({ user, onClose, onSuccess }) {
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [customPermissions, setCustomPermissions] = useState(
    user.custom_permissions || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      
      await usersAPI.updatePermissions(user.id, {
        role: selectedRole,
        custom_permissions: customPermissions,
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error actualizando permisos:', error);
      setError(error.response?.data?.error || 'Error al actualizar permisos');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permission) => {
    setCustomPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission]
    );
  };

  const permissionGroups = {
    'Dashboard': ['view_dashboard'],
    'Clientes': [
      'view_clients',
      'create_clients',
      'edit_clients',
      'delete_clients',
    ],
    'Cotizaciones': [
      'view_quotes',
      'create_quotes',
      'edit_quotes',
      'delete_quotes',
      'upload_quotes',
    ],
    'Órdenes': [
      'view_orders',
      'create_orders',
      'edit_orders',
      'delete_orders',
      'upload_orders',
    ],
    'Facilitación': [
      'view_facilitation',
    ],
    'Finanzas': [
      'view_finance',
      'create_invoices',
      'edit_invoices',
      'delete_invoices',
      'manage_finance',
    ],
    'Administración': [
      'manage_users',
      'approve_users',
      'view_reports',
    ],
  };

  return (
    <Modal 
      isOpen={true} 
      toggle={onClose} 
      size="lg"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={onClose}>
        <i className="ni ni-settings me-2"></i>
        Editar Permisos
      </ModalHeader>
      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Info del usuario */}
          <div className="card shadow border-0 mb-4">
            <div className="card-body p-3">
              <div className="d-flex align-items-center">
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.full_name}
                    className="avatar avatar-lg rounded-circle me-3 shadow"
                  />
                ) : (
                  <div className="avatar avatar-lg bg-gradient-primary rounded-circle me-3 shadow">
                    <i className="ni ni-single-02 text-white" style={{ fontSize: '1.5rem', lineHeight: '48px' }}></i>
                  </div>
                )}
                <div>
                  <h5 className="mb-0">{user.full_name}</h5>
                  <p className="text-sm text-muted mb-0">{user.email}</p>
                  <span className="badge badge-sm bg-gradient-info mt-1">
                    {ROLE_NAMES[user.role]}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <Alert color="danger" className="shadow-sm">
              <span className="alert-icon"><i className="ni ni-fat-remove"></i></span>
              <span className="alert-text">{error}</span>
            </Alert>
          )}

          {/* Cambiar rol */}
          <FormGroup>
            <Label className="form-control-label text-uppercase text-xs font-weight-bold">
              <i className="ni ni-badge me-2"></i>
              Cambiar Rol
            </Label>
            <Input
              type="select"
              className="form-control-alternative"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={loading}
            >
              <option value="facilitator">🎓 Facilitador</option>
              <option value="sales">💼 Ventas</option>
              <option value="admin">👔 Administrador</option>
              <option value="finance">💰 Finanzas</option>
              <option value="viewer">👁️ Visualizador</option>
              <option value="custom">⚙️ Personalizado</option>
            </Input>
            <small className="text-muted d-block mt-2">
              {selectedRole === 'facilitator' && '🎓 Solo acceso a módulo de Facilitación'}
              {selectedRole === 'sales' && '💼 Acceso completo excepto Administración'}
              {selectedRole === 'admin' && '👔 Acceso completo al sistema'}
              {selectedRole === 'finance' && '💰 Gestionar finanzas y facturas'}
              {selectedRole === 'viewer' && '👁️ Solo visualización, sin edición'}
              {selectedRole === 'custom' && '⚙️ Permisos personalizados abajo'}
            </small>
          </FormGroup>

          {/* Permisos personalizados */}
          <div className="card shadow border-0 mt-3">
            <div className="card-header bg-gradient-primary">
              <h6 className="mb-0 text-white">
                <i className="ni ni-lock-circle-open me-2"></i>
                Permisos Personalizados Adicionales
              </h6>
            </div>
            <div className="card-body p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <div className="row">
                {Object.entries(permissionGroups).map(([group, permissions]) => (
                  <div key={group} className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      {group === 'Facilitación' && '🎓 '}
                      {group === 'Dashboard' && '📊 '}
                      {group === 'Clientes' && '🏢 '}
                      {group === 'Cotizaciones' && '📄 '}
                      {group === 'Órdenes' && '📦 '}
                      {group === 'Finanzas' && '💰 '}
                      {group === 'Administración' && '⚙️ '}
                      {group}
                    </h6>
                    {permissions.map((permission) => (
                      <div key={permission} className="custom-control custom-checkbox mb-2">
                        <input
                          className="custom-control-input"
                          type="checkbox"
                          id={`perm-${permission}`}
                          checked={customPermissions.includes(permission)}
                          onChange={() => togglePermission(permission)}
                          disabled={loading}
                        />
                        <label className="custom-control-label text-sm" htmlFor={`perm-${permission}`}>
                          {PERMISSIONS[permission]}
                        </label>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <small className="text-muted d-block mt-3">
                <i className="ni ni-bulb-61 me-1"></i>
                Los permisos personalizados se suman a los del rol base
              </small>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="border-0">
          <Button color="secondary" onClick={onClose} disabled={loading}>
            <i className="ni ni-fat-remove me-2"></i>
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="ni ni-check-bold me-2"></i>
                Guardar Cambios
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default UserPermissionsModal;
